import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  addDoc, arrayUnion, collection, deleteField, getDoc, onSnapshot, runTransaction, updateDoc, doc, serverTimestamp, query, orderBy, where
} from 'firebase/firestore';
import { useLang } from '../LangContext';
import {
  adminProofMedia, customerVisibleMedia, isDisplayableMediaUrl, missingProofLabels,
  orderCustomerReportEntitlement, orderProofSatisfied,
  orderServiceLines, paymentReviewExplanation, reviewErrorMessage, undisplayableMedia,
} from '../orderServiceUtils';
import { AccessDenied, canAccess, canSeeMoney, isOrderInScope } from '../adminAccess';
import DatabaseError from '../components/DatabaseError';
import { orderLocation } from '../orderLocation';
import { coordText, locationChangeBadge } from '../locationChangeBadge';
import {
  ORDER_STATUSES, REVIEW_PENDING_STATUS, activityLabel, assignmentAuditEvent, canAssignWorker, countPendingReview, orderActivity, statusAfterUnassign, statusLabel, visibleOrders,
} from '../orderAdminUtils';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const STATUSES = ORDER_STATUSES;

const STATUS_COLORS = {
  pending:'#F59E0B', confirmed:'#3B82F6', assigned:'#8B5CF6', accepted:'#6366F1',
  worker_on_route:'#0EA5E9', in_progress:'#F97316', before_photos_uploaded:'#EC4899',
  after_photos_uploaded:'#14B8A6', pending_admin_review:'#D97706',
  changes_requested:'#DC2626', completed:'#10B981', cancelled:'#EF4444',
};

/** Small table-row badge — admin-only, mirrors the existing EXPRESS badge's styling. */
function PaymentReviewBadge({ order }) {
  if (!order?.paymentNeedsReview) return null;
  return (
    <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
      ⚠ REVIEW
    </span>
  );
}

/** Prominent banner at the top of the order detail modal — admin-only. */
function PaymentReviewBanner({ order }) {
  const explanation = paymentReviewExplanation(order);
  if (!explanation) return null;
  return (
    <div className="rounded-xl bg-red-50 border border-red-200 p-3 mb-3">
      <p className="text-sm font-bold text-red-800">⚠ {explanation.title}</p>
      <p className="text-xs text-red-700 mt-1">{explanation.body}</p>
    </div>
  );
}

// Helper: extract a display-ready field from an order doc regardless of how it was saved
function getDeceasedName(o) {
  return o.deceased?.name || o.deceasedName || o.deceased?.fullName || '';
}
function getCemeteryName(o) {
  return o.customCemeteryName || o.cemeteryName || o.graveLocation?.cemeteryName || o.cemetery?.name || '';
}
function getOrderNumber(o) {
  return o.orderNumber || ('#' + o.id.slice(-6).toUpperCase());
}

export default function OrdersPage({ user }) {
  const { tr, lang } = useLang();
  const [orders, setOrders] = useState([]);
  const [workers, setWorkers] = useState({});  // uid → name map
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [assigningOrder, setAssigningOrder] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [dbError, setDbError] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'orders'), orderBy('createdAt', 'desc')),
      snap => {
        setDbError('');
        setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      err => {
        console.error(err);
        setDbError(err?.code === 'permission-denied'
          ? 'Permission denied. Please log out and log in again as the super admin.'
          : (err?.message || 'Unable to read orders.'));
      }
    );
    return () => unsub();
  }, []);

  // Load workers for name lookup
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'users'), where('role', '==', 'worker')),
      snap => {
        const map = {};
        snap.docs.forEach(d => { map[d.id] = d.data().name || d.data().email || d.id; });
        setWorkers(map);
      },
      err => {
        console.error(err);
        setDbError(err?.code === 'permission-denied'
          ? 'Permission denied. Please log out and log in again as the super admin.'
          : (err?.message || 'Unable to read workers.'));
      }
    );
    return () => unsub();
  }, []);

  async function changeStatus(id, status) {
    await updateDoc(doc(db, 'orders', id), { status, updatedAt: serverTimestamp() });
  }

  async function assignWorker(orderId, workerId, workerName) {
    const order = orders.find(o => o.id === orderId) || { id: orderId };
    // Fast-path guard against the LOCAL (possibly a moment stale) snapshot,
    // so the UI can refuse immediately without waiting on a round trip.
    const gate = canAssignWorker(order);
    if (!gate.ok) {
      setActionMessage(gate.reason);
      return;
    }
    // Codex finding: the fast-path guard alone re-checks the local `orders`
    // listener snapshot, not the document as it exists the instant this
    // write actually lands -- a refund (or any other paymentStatus change)
    // landing in that narrow window would otherwise still go through,
    // since the write itself was a plain unconditional updateDoc. The
    // transaction re-reads the live document and re-applies the exact same
    // gate against THAT data immediately before writing, closing the race
    // without weakening any admin permission (the admin can still assign
    // any genuinely-paid order; this only narrows WHEN the write commits).
    const orderRef = doc(db, 'orders', orderId);
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(orderRef);
        if (!snap.exists()) {
          throw new Error('This order no longer exists.');
        }
        const liveGate = canAssignWorker(snap.data());
        if (!liveGate.ok) {
          throw new Error(liveGate.reason);
        }
        tx.update(orderRef, {
          workerId,
          workerName,
          status: 'assigned',
          declinedWorkerIds: [],
          trackingEvents: arrayUnion(assignmentAuditEvent('assigned', workerName)),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      // Never silently swallow -- the admin must see why nothing happened,
      // same as the fast-path guard above.
      setActionMessage(error.message || 'Could not assign worker.');
      return;
    }
    await addDoc(collection(db, 'notifications'), {
      userId: workerId,
      title: 'New job assigned',
      body: `A job has been assigned to you: ${getOrderNumber(order)}`,
      orderId,
      orderNumber: order.orderNumber || '',
      senderId: user?.email || 'admin',
      senderName: user?.name || 'PakMazar Admin',
      senderRole: 'admin',
      messageType: 'job_assigned',
      isRead: false,
      createdAt: serverTimestamp(),
    });
    setActionMessage(`Order assigned to ${workerName}. It is now visible in that worker's app.`);
    setAssigningOrder(null);
  }

  async function unassignWorker(order) {
    const previousWorkerId = order.workerId;
    await updateDoc(doc(db, 'orders', order.id), {
      workerId: deleteField(),
      workerName: deleteField(),
      status: statusAfterUnassign(order),
      declinedWorkerIds: [],
      trackingEvents: arrayUnion(assignmentAuditEvent('unassigned', order.workerName || workers[order.workerId] || 'Worker')),
      updatedAt: serverTimestamp(),
    });
    if (previousWorkerId) {
      await addDoc(collection(db, 'notifications'), {
        userId: previousWorkerId,
        title: 'Job unassigned',
        body: `${getOrderNumber(order)} was removed from your assigned jobs by the admin.`,
        orderId: order.id,
        orderNumber: order.orderNumber || '',
        senderId: user?.email || 'admin',
        senderName: user?.name || 'PakMazar Admin',
        senderRole: 'admin',
        messageType: 'job_unassigned',
        isRead: false,
        createdAt: serverTimestamp(),
      });
    }
    setActionMessage('Worker removed. The job is available for assignment again.');
    setSelectedOrder(null);
  }

  async function setArchived(order, archived) {
    await updateDoc(doc(db, 'orders', order.id), {
      adminArchived: archived,
      adminArchivedAt: archived ? serverTimestamp() : deleteField(),
      adminArchivedBy: archived ? (user?.email || 'admin') : deleteField(),
      updatedAt: serverTimestamp(),
    });
    setActionMessage(archived
      ? 'Job removed from the customer and worker apps. Payment and audit history were preserved.'
      : 'Job restored to the live system.');
    setSelectedOrder(null);
  }

  async function updateFlowerFulfillment(id, flowerFulfillmentStatus) {
    await updateDoc(doc(db, 'orders', id), {
      flowerFulfillmentStatus,
      updatedAt: serverTimestamp(),
    });
  }

  async function publishFlowerQuote(order, quoteAmount) {
    const previousQuote = Number(order.flowerQuoteAmount || 0);
    await updateDoc(doc(db, 'orders', order.id), {
      flowerQuoteAmount: quoteAmount,
      flowerPrice: quoteAmount,
      flowerQuoteStatus: 'quoted',
      totalAmount: Number(order.totalAmount || 0) - previousQuote + quoteAmount,
      status: 'pending_payment',
      paymentStatus: 'unpaid',
      updatedAt: serverTimestamp(),
    });
  }

  if (!canAccess(user, 'orders')) return <AccessDenied />;

  const showMoney = canSeeMoney(user);
  const filtered = visibleOrders(orders, showArchived).filter(o => {
    if (!isOrderInScope(user, o)) return false;
    const matchFilter = !filter || o.status === filter;
    const q = search.toLowerCase();
    const matchSearch = !search ||
      getDeceasedName(o).toLowerCase().includes(q) ||
      getOrderNumber(o).toLowerCase().includes(q) ||
      getCemeteryName(o).toLowerCase().includes(q) ||
      (workers[o.workerId] || '').toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  return (
    <div className="page-shell">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="page-title">{tr('orders')} ({filtered.length})</h1>
      </div>

      <DatabaseError message={dbError} />
      {actionMessage && <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{actionMessage}</div>}

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <input className="input-field flex-1" placeholder={tr('search')} value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input-field w-52" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">{tr('all')}</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => { setShowArchived(v => !v); setFilter(''); }} className={showArchived ? 'btn-primary' : 'btn-secondary'}>
          {showArchived ? 'Show active jobs' : `Archived (${orders.filter(o => o.adminArchived).length})`}
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-2 flex-wrap mb-6">
        <button onClick={() => setFilter('')} className={`text-xs px-3 py-1.5 rounded-full font-medium transition ${!filter ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{tr('all')}</button>
        <button
          onClick={() => setFilter(filter === REVIEW_PENDING_STATUS ? '' : REVIEW_PENDING_STATUS)}
          className={`text-xs px-3 py-1.5 rounded-full font-medium transition ${filter === REVIEW_PENDING_STATUS ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
          Pending Review{countPendingReview(orders) > 0 ? ` (${countPendingReview(orders)})` : ''}
        </button>
        {['pending','in_progress','completed','cancelled'].map(s => (
          <button key={s} onClick={() => setFilter(filter === s ? '' : s)}
            className="text-xs px-3 py-1.5 rounded-full font-medium transition"
            style={filter === s
              ? { backgroundColor: STATUS_COLORS[s], color: '#fff' }
              : { backgroundColor: (STATUS_COLORS[s] || '#6B7280') + '15', color: STATUS_COLORS[s] || '#6B7280' }
            }
          >{s.replace(/_/g,' ')}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto -mx-1 px-1"><table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {[tr('orderNo'), tr('deceased'), tr('cemetery'), tr('worker'), tr('amount'), tr('status'), tr('date'), tr('actions')].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(o => {
              const deceasedName = getDeceasedName(o);
              const cemeteryName = getCemeteryName(o);
              const orderNum = getOrderNumber(o);
              const workerName = o.workerName || workers[o.workerId] || '';
              return (
              <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50 transition">
                <td className="px-4 py-3 font-mono text-xs text-gray-500">{orderNum}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{deceasedName || <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-3 text-gray-600">
                  {cemeteryName || <span className="text-gray-300">—</span>}
                  <LocationFlag order={o} />
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {workerName
                    ? <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full">{workerName}</span>
                    : <button onClick={() => setAssigningOrder(o)} className="text-xs bg-purple-50 text-purple-600 hover:bg-purple-100 px-2 py-1 rounded-lg">+ Assign</button>
                  }
                </td>
                <td className="px-4 py-3 font-semibold text-primary">{showMoney ? `${o.totalAmount?.toFixed(2) || '?'} ₼` : 'Hidden'}</td>
                <td className="px-4 py-3">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{
                    backgroundColor: (STATUS_COLORS[o.status] || '#6B7280') + '20',
                    color: STATUS_COLORS[o.status] || '#6B7280'
                  }}>{statusLabel(o.status, lang)}</span>
                  {o.isExpress && (
                    <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">⚡ EXPRESS</span>
                  )}
                  <PaymentReviewBadge order={o} />
                </td>
                <td className="px-4 py-3 text-xs text-gray-400">
                  {o.createdAt?.toDate ? o.createdAt.toDate().toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => setSelectedOrder(o)} className="text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-1 rounded-lg transition">{tr('details')}</button>
                    {!showArchived && <button onClick={() => setAssigningOrder(o)} className="text-xs bg-purple-50 text-purple-700 hover:bg-purple-100 px-2 py-1 rounded-lg transition">{workerName ? 'Reassign' : 'Assign'}</button>}
                    {showArchived && <button onClick={() => setArchived(o, false)} className="text-xs bg-green-50 text-green-700 hover:bg-green-100 px-2 py-1 rounded-lg transition">Restore</button>}
                    <button onClick={() => setEditingLocation(o)} className="text-xs bg-orange-50 text-orange-600 hover:bg-orange-100 px-2 py-1 rounded-lg transition">📍</button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table></div>
        {filtered.length === 0 && (
          <p className="text-center text-gray-400 py-16">{tr('noData')}</p>
        )}
      </div>

      {selectedOrder && (
        <OrderDetailModal order={selectedOrder} tr={tr} showMoney={showMoney} onClose={() => setSelectedOrder(null)} onStatusChange={changeStatus} onFlowerStatusChange={updateFlowerFulfillment} onFlowerQuote={publishFlowerQuote} onReassign={() => { setAssigningOrder(selectedOrder); setSelectedOrder(null); }} onUnassign={unassignWorker} onArchive={setArchived} />
      )}
      {editingLocation && (
        <LocationModal order={editingLocation} tr={tr} onClose={() => setEditingLocation(null)} />
      )}
      {assigningOrder && (
        <AssignWorkerModal
          order={assigningOrder}
          workers={Object.entries(workers).map(([uid, name]) => ({ uid, name }))}
          onAssign={assignWorker}
          onClose={() => setAssigningOrder(null)}
        />
      )}
    </div>
  );
}

function AssignWorkerModal({ order, workers, onAssign, onClose }) {
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);
  const gate = canAssignWorker(order);
  async function save() {
    if (!selected || !gate.ok) return;
    setSaving(true);
    const w = workers.find(w => w.uid === selected);
    await onAssign(order.id, selected, w?.name || selected);
    setSaving(false);
  }
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Assign Worker</h3>
        <p className="text-xs text-gray-500 mb-4">
          Order: <span className="font-mono font-semibold">{getOrderNumber(order)}</span>
        </p>
        {!gate.ok
          ? <p className="text-amber-700 text-sm bg-amber-50 border border-amber-200 rounded-lg py-3 px-3 mb-4">{gate.reason}</p>
          : workers.length === 0
          ? <p className="text-gray-400 text-sm py-4 text-center">No workers found. Add workers first.</p>
          : <select className="input-field w-full mb-4" value={selected} onChange={e => setSelected(e.target.value)}>
              <option value="">— Select worker —</option>
              {workers.map(w => <option key={w.uid} value={w.uid}>{w.name}</option>)}
            </select>
        }
        <div className="flex gap-2 justify-end mt-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={save} disabled={!gate.ok || !selected || saving} className="btn-primary">
            {saving ? 'Saving...' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderDetailModal({ order: o, tr, showMoney, onClose, onStatusChange, onFlowerStatusChange, onFlowerQuote, onReassign, onUnassign, onArchive }) {
  const [status, setStatus] = useState(o.status);
  const [saving, setSaving] = useState(false);
  const [quote, setQuote] = useState(o.flowerQuoteAmount || '');
  const [privateContactPhone, setPrivateContactPhone] = useState('');

  // contactPhone lives in orders/{orderId}/private/contact, never on the
  // main order document `o` -- see firebase/firestore.rules' "Order
  // private contact" section for why (so an unassigned worker browsing
  // new work can never receive it). A one-time getDoc, not a listener:
  // this field is write-once at order creation, never updated after.
  // Same admin authorization (adminCan('orders')/'finance'/
  // 'communications') the main order read already requires -- a
  // permission-denied (shouldn't happen for an admin who can already see
  // this modal) or a missing doc (no phone was given, or a legacy order
  // predating this subcollection) both resolve to '', never a thrown
  // error visible to the admin.
  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'orders', o.id, 'private', 'contact'))
      .then(snap => {
        if (!cancelled) setPrivateContactPhone(snap.exists() ? (snap.data().contactPhone || '') : '');
      })
      .catch(() => { if (!cancelled) setPrivateContactPhone(''); });
    return () => { cancelled = true; };
  }, [o.id]);

  async function save() {
    setSaving(true);
    await onStatusChange(o.id, status);
    setSaving(false);
  }

  const row = (label, value) => value ? (
    <div key={label} className="flex gap-2 py-2 border-b border-gray-50">
      <span className="text-xs text-gray-400 w-32 shrink-0">{label}</span>
      <span className="text-sm text-gray-800 font-medium">{value}</span>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900">{tr('details')} · {getOrderNumber(o)}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="p-6 space-y-1">
          <PaymentReviewBanner order={o} />
          {row(tr('deceased'), getDeceasedName(o))}
          {row(tr('cemetery'), getCemeteryName(o))}
          {row(o.cityType === 'custom' ? 'Custom City' : 'City', orderLocation(o).city)}
          {o.cemeteryType === 'custom' && <>
            {row('Custom Cemetery', orderLocation(o).cemetery)}
            {row('Address / notes', orderLocation(o).address)}
            {row('Cemetery coordinates', orderLocation(o).coordinates)}
            {orderLocation(o).url && <a className="btn-secondary my-2" href={orderLocation(o).url} target="_blank" rel="noreferrer">Open in Google Maps</a>}
          </>}
          {row('Order status', o.status)}
          {row(tr('graveDesc'), o.graveLocation?.description)}
          {row(tr('graveCoords'), o.graveLocation?.lat ? `${o.graveLocation.lat}, ${o.graveLocation.lng}` : null)}
          {o.customerLocationChoice === 'confirmed' && (
            <div className="flex gap-2 py-2 border-b border-gray-50 items-center">
              <span className="text-xs text-gray-400 w-32 shrink-0">Location choice</span>
              <span className="text-sm font-medium text-green-700">Müştəri məzar yerini təsdiqləyib</span>
            </div>
          )}
          {o.customerLocationChoice === 'uncertain' && (
            <div className="flex gap-2 py-2 border-b border-gray-50 items-center">
              <span className="text-xs text-gray-400 w-32 shrink-0">Location choice</span>
              <span className="text-sm font-medium text-amber-700">⚠ Müştəri məzar yerindən əmin deyil</span>
            </div>
          )}
          {row('Customer instructions', o.customerInstructions)}
          {row('Contact number', privateContactPhone)}
          <AudioLink url={o.voiceInstructionUrl} label="Customer voice instructions (original, attached at order creation)" />
          <OrderVoiceMessages orderId={o.id} />
          {row(tr('worker'), o.workerName)}
          {row(tr('customer'), o.customerName || o.phone || o.customerEmail || o.customerId)}
          {row('Customer email', o.customerEmail)}
          {row('Customer ID', o.customerId)}
          <OrderServicePanel order={o} showMoney={showMoney} />
          {o.flowerId && <>
            {row('Flowers', `${o.flowerName} (+${o.flowerPrice || 0} ₼)`)}
            {o.flowerSelectionType === 'custom' && <>
              {row('Custom request', o.customFlowerDescription)}
              {o.customFlowerReferenceImages?.length > 0 && <div className="grid grid-cols-3 gap-2 py-2">
                {o.customFlowerReferenceImages.map((url, i) => <a key={i} href={url} target="_blank" rel="noreferrer"><img src={url} alt="Customer reference" className="w-full h-20 object-cover rounded-lg" /></a>)}
              </div>}
              {o.customFlowerReferenceVideo && <a className="text-sm text-blue-600 underline" href={o.customFlowerReferenceVideo} target="_blank" rel="noreferrer">Open customer reference video</a>}
            </>}
            {/* A catalog flower_catalog entry with priceType 'from' also lands
                here as flowerSelectionType 'quote' (Codex finding, round 1) --
                the quote input must not be restricted to 'custom' or those
                orders can never leave awaiting_quote/unpaid. */}
            {(o.flowerSelectionType === 'custom' || o.flowerSelectionType === 'quote') && <>
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-100 p-3">
                <p className="text-xs font-semibold text-amber-900 mb-2">
                  {o.flowerSelectionType === 'quote' ? 'Flower catalog quote' : 'Custom flower quote'}
                </p>
                <div className="flex gap-2">
                  <input className="input-field flex-1" type="number" min="0" step="0.01" placeholder="AZN" value={quote} onChange={e => setQuote(e.target.value)} />
                  <button className="btn-primary" disabled={!quote || Number(quote) < 0} onClick={() => onFlowerQuote(o, Number(quote))}>Publish quote</button>
                </div>
                <p className="text-xs text-amber-800 mt-2">Publishing makes the customer payment action available.</p>
              </div>
            </>}
            {row('Worker flower update', o.flowerFulfillmentStatus || 'awaiting_worker_confirmation')}
            {row('Worker alternative', o.flowerAlternative)}
            <div className="pt-3">
              <p className="text-xs text-gray-500 mb-2">Admin flower decision — communicate with the customer yourself; the worker never contacts them.</p>
              <select className="input-field w-full" value={o.flowerFulfillmentStatus || 'awaiting_worker_confirmation'} onChange={e => onFlowerStatusChange(o.id, e.target.value)}>
                <option value="awaiting_worker_confirmation">Awaiting worker confirmation</option>
                <option value="available">Flower arrangement available</option>
                <option value="alternative_proposed">Alternative awaiting admin decision</option>
                <option value="alternative_approved">Alternative approved by admin</option>
                <option value="cancelled_by_admin">Flower add-on cancelled by admin</option>
              </select>
            </div>
          </>}
          {row(tr('payment'), o.paymentStatus)}
          {row(tr('scheduledDate'), o.scheduledDate?.toDate ? o.scheduledDate.toDate().toLocaleDateString() : o.scheduledDate)}
          {o.trackingEvents?.length > 0 && (
            <div className="pt-3">
              <p className="text-xs font-semibold text-gray-500 mb-2">{tr('trackingEvents')}</p>
              <div className="space-y-1">
                {o.trackingEvents.map((ev, i) => (
                  <div key={i} className="flex gap-2 text-xs text-gray-500">
                    <span className="text-gray-300">{i + 1}.</span>
                    <span>{typeof ev === 'string' ? ev : JSON.stringify(ev)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <OrderLocationPanel order={o} />
          <OrderFactsPanel order={o} />
          <OrderClientReportPanel order={o} />
          <OrderProofPanel order={o} label={tr('photos')} />
          <OrderActivityPanel order={o} />
          <OrderReviewPanel order={o} />
          <OrderStopPanel order={o} />
          <div className="pt-4">
            <p className="text-xs font-semibold text-gray-500 mb-2">{tr('changeStatus')}</p>
            <div className="flex gap-2">
              <select className="input-field flex-1" value={status} onChange={e => setStatus(e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={save} disabled={saving} className="btn-primary">{saving ? '...' : tr('save')}</button>
            </div>
          </div>
          <div className="pt-4 mt-4 border-t border-gray-100 flex flex-wrap gap-2">
            {!o.adminArchived && <button className="btn-secondary" onClick={onReassign}>{o.workerId ? 'Reassign worker' : 'Assign worker'}</button>}
            {!o.adminArchived && o.workerId && <button className="btn-secondary" onClick={() => onUnassign(o)}>Unassign worker</button>}
            <button className={o.adminArchived ? 'btn-primary' : 'btn-danger'} onClick={() => onArchive(o, !o.adminArchived)}>
              {o.adminArchived ? 'Restore job' : 'Remove / archive job'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LocationModal({ order: o, tr, onClose }) {
  const [lat, setLat] = useState(o.graveLocation?.lat || '');
  const [lng, setLng] = useState(o.graveLocation?.lng || '');
  const [desc, setDesc] = useState(o.graveLocation?.description || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    const nextLat = parseFloat(lat);
    const nextLng = parseFloat(lng);
    // An admin override is authoritative, but it is never silent: it closes
    // any pending proposal and appends to the same audit trail the worker and
    // customer write to.
    await updateDoc(doc(db, 'orders', o.id), {
      'graveLocation.lat': Number.isFinite(nextLat) ? nextLat : null,
      'graveLocation.lng': Number.isFinite(nextLng) ? nextLng : null,
      'graveLocation.description': desc,
      ...(o.originalGraveLocation ? {} : { originalGraveLocation: o.graveLocation || {} }),
      locationChangeStatus: o.locationChangeStatus === 'pending'
        ? 'admin_override'
        : (o.locationChangeStatus || 'none'),
      locationHistory: arrayUnion({
        type: 'location_admin_override',
        oldLocation: o.graveLocation || {},
        newLocation: {
          lat: Number.isFinite(nextLat) ? nextLat : null,
          lng: Number.isFinite(nextLng) ? nextLng : null,
          description: desc,
        },
        changedBy: auth.currentUser?.email || auth.currentUser?.uid || 'admin',
        changedByRole: 'admin',
        reason: reason.trim(),
        status: 'admin_override',
        createdAt: new Date().toISOString(),
      }),
      updatedAt: serverTimestamp(),
    });
    setSaving(false);
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="font-bold text-gray-900 mb-4">📍 {tr('editLocation')}</h3>
        <div className="space-y-3">
          <input className="input-field w-full" placeholder="Description / Grave row" value={desc} onChange={e => setDesc(e.target.value)} />
          <div className="flex gap-2">
            <input className="input-field flex-1" placeholder="Latitude" value={lat} onChange={e => setLat(e.target.value)} />
            <input className="input-field flex-1" placeholder="Longitude" value={lng} onChange={e => setLng(e.target.value)} />
          </div>
          <input className="input-field w-full" placeholder="Reason for this override (stored in the audit trail)"
                 value={reason} onChange={e => setReason(e.target.value)} />
          {lat && lng && (
            <a href={`https://maps.google.com/?q=${lat},${lng}`} target="_blank" rel="noreferrer"
               className="text-xs text-blue-600 hover:underline">🗺 View on Google Maps</a>
          )}
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-secondary">{tr('cancel')}</button>
          <button onClick={save} disabled={saving} className="btn-primary">{saving ? '...' : tr('save')}</button>
        </div>
      </div>
    </div>
  );
}


/**
 * SERVICE and ADD-ONS for an order.
 *
 * Everything comes from the snapshot frozen onto the order at checkout, so a
 * later price change in Admin -> Packages never rewrites a historical total.
 * Add-ons are listed as their own labelled lines, never as raw JSON.
 */
export function OrderServicePanel({ order, showMoney }) {
  const { lang, tr } = useLang();
  const lines = orderServiceLines(order, lang);
  const money = value => (showMoney ? `${Number(value || 0).toFixed(2)} ₼` : '—');
  // null (not a stored `false`) means this order predates the green-area
  // feature, or was placed against a city with no green-aware entries yet
  // -- shown as "—", never as a confirmed "No" (see orderServiceLines).
  const greenAreaLabel = lines.greenArea === true ? tr('greenYes')
    : lines.greenArea === false ? tr('greenNo') : '—';

  return (
    <div className="pt-3">
      <p className="text-xs font-semibold text-gray-500 mb-2">SERVICE</p>
      <div className="rounded-xl border border-gray-100 divide-y divide-gray-50">
        <div className="flex items-center justify-between px-3 py-2">
          <div>
            <p className="text-sm font-semibold text-gray-900">{lines.packageName}</p>
            <p className="text-[11px] text-gray-400">
              {lines.isAnnual ? 'Annual plan' : 'One-time service'}
              {lines.visitLabel ? ` · ${lines.visitLabel}` : ''}
            </p>
            <p className="text-[11px] text-gray-400">{tr('greenArea')}: {greenAreaLabel}</p>
          </div>
          <span className="text-sm font-bold text-primary">{money(lines.packagePrice)}</span>
        </div>

        {lines.includedServices.length > 0 && (
          <div className="px-3 py-2">
            <p className="text-[11px] font-semibold text-gray-400 mb-1">INCLUDED</p>
            <ul className="text-xs text-gray-600 space-y-0.5">
              {lines.includedServices.map((item, i) => <li key={i}>· {item}</li>)}
            </ul>
          </div>
        )}

        {lines.flower && (
          <div className="flex items-center justify-between px-3 py-2">
            <div>
              <p className="text-[11px] font-semibold text-gray-400">FLOWERS</p>
              <p className="text-sm text-gray-800">{lines.flower.name}</p>
            </div>
            <span className="text-sm font-semibold text-gray-700">
              {lines.flower.quoted ? 'quote pending' : money(lines.flower.price)}
            </span>
          </div>
        )}

        {lines.addOns.length > 0 && (
          <div className="px-3 py-2">
            <p className="text-[11px] font-semibold text-gray-400 mb-1">ADDITIONAL SERVICES</p>
            {lines.addOns.map((a, i) => (
              <div key={i} className="flex items-center justify-between py-0.5">
                <span className="text-sm text-gray-800">{a.name}</span>
                <span className="text-sm font-semibold text-gray-700">
                  {a.quoted ? 'quote pending' : money(a.price)}
                </span>
              </div>
            ))}
          </div>
        )}

        {lines.isExpress && (
          <div className="flex items-center justify-between px-3 py-2 bg-amber-50">
            <span className="text-sm font-semibold text-amber-800">⚡ Express fee</span>
            <span className="text-sm font-bold text-amber-800">{money(lines.expressFee)}</span>
          </div>
        )}

        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-b-xl">
          <span className="text-sm font-bold text-gray-900">Total paid</span>
          <span className="text-sm font-bold text-primary">
            {showMoney ? money(order.totalAmount) : 'Hidden for this admin'}
          </span>
        </div>
      </div>
      {lines.hasPendingQuote && (
        <p className="text-[11px] text-amber-700 mt-2">
          This order contains an item priced &ldquo;from&rdquo;. It was not charged
          automatically and needs a quote before it can be paid.
        </p>
      )}
    </div>
  );
}

/** A grid of media thumbnails plus a diagnostic for unusable stored values. */
function MediaGrid({ urls, title }) {
  if (!urls || urls.length === 0) return null;
  const broken = undisplayableMedia(urls);
  const usable = urls.filter(u => !broken.includes(u));
  return (
    <div className="mt-2">
      <p className="text-[11px] font-semibold text-gray-400 mb-1">{title}</p>
      {usable.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {usable.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer">
              <img src={url} alt={title} className="w-full h-24 object-cover rounded-xl bg-gray-100" />
            </a>
          ))}
        </div>
      )}
      {broken.length > 0 && (
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-2">
          <p className="text-[11px] font-semibold text-red-700">
            {broken.length} item(s) were never uploaded to Firebase Storage.
          </p>
          <p className="text-[11px] text-red-600 break-all">
            The order stores a device path instead of a download URL: {broken.join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}

function VideoLink({ url, label }) {
  if (!url) return null;
  if (!undisplayableMedia([url]).length) {
    return (
      <div className="mt-3">
        <video src={url} controls className="w-full max-h-64 rounded-xl bg-black" />
        <a href={url} target="_blank" rel="noreferrer"
          className="btn-secondary inline-block mt-2">{label}</a>
      </div>
    );
  }
  return (
    <p className="mt-2 text-[11px] text-red-600 break-all">
      Video was never uploaded to Firebase Storage — the order stores a device
      path instead of a download URL: {url}
    </p>
  );
}

/** Native browser play/pause for the customer's recorded voice instruction. */
function AudioLink({ url, label }) {
  if (!url) return null;
  if (!isDisplayableMediaUrl(url)) {
    return (
      <p className="mt-2 text-[11px] text-red-600 break-all">
        Voice instruction was never uploaded to Firebase Storage — the order
        stores a device path instead of a download URL: {url}
      </p>
    );
  }
  return (
    <div className="pt-3">
      <p className="text-xs font-semibold text-gray-500 mb-2">{label}</p>
      {/* Native <audio controls> gives play/pause/seek with no extra library. */}
      <audio src={url} controls className="w-full" style={{ height: 36 }} />
    </div>
  );
}

/**
 * The order conversation's voice messages (customer → worker and worker →
 * customer), distinct from the original voice instruction shown above by
 * AudioLink. A single `where('orderId', '==', orderId)` with no `orderBy`
 * needs no new composite index — sorting and the `type === 'voice'` filter
 * both happen client-side, matching how firestore_service.dart already
 * queries this same collection elsewhere. Read access is granted by the
 * existing order_messages security rule (adminCan('orders') /
 * adminCan('communications')), so no rule or index change is required.
 */
function OrderVoiceMessages({ orderId }) {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    // A single where('orderId', '==', ...) with no orderBy: the same
    // deliberate pattern firestore_service.dart already uses for this
    // collection (see markOrderChatNotificationsRead / getUserConversations)
    // to guarantee no new composite index is needed. Sorting happens
    // client-side below instead.
    const q = query(
      collection(db, 'order_messages'),
      where('orderId', '==', orderId)
    );
    const unsub = onSnapshot(
      q,
      snap => {
        const at = m => m.createdAt?.toDate ? m.createdAt.toDate().getTime() : 0;
        setMessages(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(m => m.type === 'voice' && isDisplayableMediaUrl(m.attachmentUrl))
            .sort((a, b) => at(a) - at(b))
        );
      },
      () => setMessages([])
    );
    return unsub;
  }, [orderId]);

  if (messages.length === 0) return null;

  const senderLabel = m => {
    const role = m.senderRole === 'worker' ? 'Worker' : 'Customer';
    return m.senderName ? `${role} — ${m.senderName}` : role;
  };
  const when = m => (m.createdAt?.toDate ? m.createdAt.toDate().toLocaleString() : '');

  return (
    <div className="pt-3">
      <p className="text-xs font-semibold text-gray-500 mb-2">
        Conversation voice messages ({messages.length})
      </p>
      <div className="space-y-2">
        {messages.map(m => (
          <div key={m.id} className="rounded-lg bg-gray-50 p-2 border border-gray-100">
            <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
              <span className="font-semibold text-gray-700">{senderLabel(m)}</span>
              <span>{when(m)}</span>
            </div>
            <audio src={m.attachmentUrl} controls className="w-full" style={{ height: 36 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * STOP SERVICE — the admin ends a live service.
 *
 * Stopping is not completion and not deletion: the order keeps its history and
 * its worker assignment, leaves every active queue, and both the worker and
 * the customer are shown the same reason. It never moves money — a refund is a
 * separate decision made on the complaint, so a mis-click cannot refund
 * anyone.
 */
export function OrderStopPanel({ order }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const status = order.status;
  const alreadyStopped = status === 'admin_cancelled';
  const finished = status === 'completed' || status === 'cancelled';
  const paid = String(order.paymentStatus || '').toLowerCase() === 'paid';
  const refundable = Math.max(
    0,
    Math.round((Number(order.totalAmount || 0) - Number(order.refundedAmount || 0)) * 100) / 100,
  );

  async function stop() {
    setError('');
    if (reason.trim().length < 5) {
      setError('A reason of at least 5 characters is required — the worker and the customer both see it.');
      return;
    }
    setBusy(true);
    try {
      const call = httpsCallable(functions, 'stopOrder');
      await call({ orderId: order.id, reason: reason.trim() });
      setOpen(false);
      setReason('');
    } catch (e) {
      setError(reviewErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (alreadyStopped) {
    return (
      <div className="pt-4">
        <p className="text-xs font-semibold text-gray-500 mb-2">SERVICE STOPPED</p>
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-sm text-red-800">
            Stopped by PakMazar Team{order.cancelledByEmail ? ` (${order.cancelledByEmail})` : ''}.
          </p>
          {order.cancellationReason && (
            <p className="text-sm text-red-700 mt-1">
              <span className="text-[11px] font-semibold text-red-500">REASON: </span>
              {order.cancellationReason}
            </p>
          )}
          {order.refundReviewRequired && (
            <p className="text-xs text-amber-700 mt-2 font-semibold">
              This order was paid and has not been refunded. A refund decision
              is outstanding — handle it through the customer&apos;s complaint.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (finished) return null;

  return (
    <div className="pt-4">
      <p className="text-xs font-semibold text-gray-500 mb-2">STOP SERVICE</p>
      {!open ? (
        <button className="btn-danger" onClick={() => setOpen(true)}>
          Stop Service
        </button>
      ) : (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2">
          <p className="text-sm font-semibold text-red-800">
            Stop this service?
          </p>
          <p className="text-xs text-red-700">
            The worker loses the job immediately and both the worker and the
            customer are shown your reason. The order is kept in history and
            nothing is refunded by this action.
            {paid && refundable > 0 && (
              <span className="block mt-1 font-semibold">
                This order is paid. {refundable} AZN would remain refundable —
                refund it through the customer&apos;s complaint if appropriate.
              </span>
            )}
          </p>
          <textarea
            className="input-field w-full"
            rows={3}
            placeholder="Reason shown to the worker and the customer — e.g. The customer asked us to stop this service."
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              className="btn-secondary flex-1"
              disabled={busy}
              onClick={() => { setOpen(false); setError(''); }}>
              Cancel
            </button>
            <button className="btn-danger flex-1" disabled={busy} onClick={stop}>
              {busy ? '...' : 'Stop Service'}
            </button>
          </div>
          {error && <p className="text-red-600 text-xs">{error}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * ACTIVITY — the order's real lifecycle, oldest first.
 *
 * Built only from stored `trackingEvents` and `statusHistory`. Nothing is
 * inferred from the current status, so a gap in the trail shows as a gap
 * rather than as an invented step.
 */
export function OrderActivityPanel({ order }) {
  const rows = orderActivity(order);

  const when = at => (at ? new Date(at).toLocaleString() : '—');

  return (
    <div className="pt-4">
      <p className="text-xs font-semibold text-gray-500 mb-2">ACTIVITY HISTORY</p>
      {rows.length === 0 ? (
        <p className="text-[11px] text-gray-400">
          No lifecycle events are stored on this order.
        </p>
      ) : (
        <ol className="rounded-xl border border-gray-100 divide-y divide-gray-50">
          {rows.map((row, i) => (
            <li key={i} className="px-3 py-2 text-xs">
              <div className="flex items-start justify-between gap-3">
                <span className="font-semibold text-gray-700">
                  {activityLabel(row.event)}
                </span>
                <span className="text-gray-400 whitespace-nowrap">{when(row.at)}</span>
              </div>
              {(row.actorRole || row.actorId) && (
                <p className="text-gray-500">
                  {row.actorRole || 'unknown'}{row.actorId ? ` \u00b7 ${row.actorId}` : ''}
                </p>
              )}
              {row.reason && <p className="text-gray-600 mt-0.5">{row.reason}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * A compact facts strip: where the order stands and whether it was paid.
 */
export function OrderFactsPanel({ order }) {
  const { lang, tr } = useLang();
  const cell = (label, value, tone = 'text-gray-800') => (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-sm truncate ${tone}`}>{value || '\u2014'}</p>
    </div>
  );
  const paid = String(order.paymentStatus || '').toLowerCase() === 'paid';
  return (
    <div className="pt-4 grid grid-cols-2 gap-3">
      {cell(tr('status'), statusLabel(order.status, lang))}
      {cell(
        tr('payment'),
        order.paymentStatus || 'unpaid',
        paid ? 'text-green-700 font-semibold' : 'text-amber-700 font-semibold',
      )}
      {cell(tr('customer'), order.customerName || order.customerEmail || order.customerId)}
      {cell(tr('worker'), order.workerName || order.workerId || 'unassigned')}
    </div>
  );
}

/**
 * ADMIN PROOF — the internal evidence PakMazar Admin approves against.
 *
 * Before/after photos are required on EVERY package, so this panel never says
 * "not required" for them. Whether the customer bought a report is a different
 * question entirely and is answered by OrderClientReportPanel below.
 */
export function OrderProofPanel({ order, label }) {
  const { before, after, video } = adminProofMedia(order);

  const chip = (satisfied, text) => (satisfied
    ? <span className="badge bg-green-100 text-green-700">{text}: uploaded</span>
    : <span className="badge bg-red-100 text-red-700">{text}: MISSING</span>);

  return (
    <div className="pt-4">
      <p className="text-xs font-semibold text-gray-500 mb-1">
        ADMIN PROOF {label ? `· ${label}` : ''}
      </p>
      <p className="text-[11px] text-gray-400 mb-2">
        Internal evidence for approval. EVERY package — Basic, Standard,
        Premium and every annual visit — requires 2 before photos,
        2 after photos and a completion video.
      </p>
      <div className="flex flex-wrap gap-2">
        {chip(before.length >= 2, 'Before photos')}
        {chip(after.length >= 2, 'After photos')}
        {chip(video.length > 0, 'Completion video')}
      </div>
      <MediaGrid urls={before} title="BEFORE" />
      <MediaGrid urls={after} title="AFTER" />
      <VideoLink url={video} label="Open admin proof video" />
    </div>
  );
}

/**
 * CUSTOMER REPORT — read-only. What the customer actually sees.
 *
 * Never approval evidence: an admin approves against ADMIN PROOF above.
 * There is only one upload now (`photos.*`, shown in OrderProofPanel) --
 * for a NEW order this panel shows exactly that SAME media, filtered by
 * entitlement, since that is what the customer's own order-detail screen
 * displays too. An OLD in-flight/completed order that already has a
 * separate `clientReport.*` (the retired two-stage flow) still shows that
 * historical data instead -- see customerVisibleMedia().
 */
export function OrderClientReportPanel({ order }) {
  const entitlement = orderCustomerReportEntitlement(order);
  const { before, after, video, sentAt } = customerVisibleMedia(order);
  const has = before.length + after.length > 0 || Boolean(video);

  const sentText = (() => {
    if (!sentAt) return 'Not sent yet';
    if (typeof sentAt === 'string') return `Sent ${new Date(sentAt).toLocaleString()}`;
    if (sentAt.toDate) return `Sent ${sentAt.toDate().toLocaleString()}`;
    return 'Sent';
  })();

  return (
    <div className="pt-4">
      <p className="text-xs font-semibold text-gray-500 mb-1">CUSTOMER REPORT</p>
      <p className="text-[11px] text-gray-400 mb-2">
        Read-only. This is what the customer receives; it is never used to
        approve the job. {sentText}.
      </p>
      <div className="flex flex-wrap gap-2">
        <span className="badge bg-gray-100 text-gray-600">
          Entitlement: {entitlement.photoRequired ? 'photos' : 'no photos'}
          {entitlement.videoRequired ? ' + video' : ''}
        </span>
      </div>
      {!has && (
        <p className="text-[11px] text-gray-400 mt-2">
          {entitlement.photoRequired
            ? 'No customer media has been uploaded for this order yet.'
            : 'This package includes no customer media report.'}
        </p>
      )}
      <MediaGrid urls={before} title="CUSTOMER BEFORE" />
      <MediaGrid urls={after} title="CUSTOMER AFTER" />
      <VideoLink url={video} label="Open customer video" />
    </div>
  );
}


/** Small list indicator. Full detail lives in the order detail view. */
function LocationFlag({ order }) {
  const status = order.locationChangeStatus || 'none';
  if (status === 'pending') {
    return <span className="badge bg-amber-100 text-amber-700 ml-2">Change pending</span>;
  }
  if (status === 'approved') {
    return <span className="badge bg-green-100 text-green-700 ml-2">Location confirmed</span>;
  }
  if (status === 'admin_override') {
    return <span className="badge bg-purple-100 text-purple-700 ml-2">Admin set</span>;
  }
  return null;
}

const mapsHref = location => {
  const text = coordText(location);
  return text ? `https://www.google.com/maps/search/?api=1&query=${text.replace(/\s/g, '')}` : '';
};

const historyTime = value => {
  if (!value) return '';
  if (typeof value === 'string') return new Date(value).toLocaleString();
  if (value.toDate) return value.toDate().toLocaleString();
  return '';
};

/**
 * LOCATION — the original customer pin, the currently active location, any
 * worker proposal, and the full audit trail.
 *
 * Admin is the only place worker identity is shown; customers only ever see
 * "PakMazar officer".
 */
export function OrderLocationPanel({ order }) {
  const loc = orderLocation(order);
  const original = order.originalGraveLocation || order.graveLocation;
  const active = order.graveLocation;
  const proposed = order.pendingGraveLocation;
  const history = Array.isArray(order.locationHistory) ? order.locationHistory : [];

  const badge = locationChangeBadge(order);

  const block = (label, location, tone, extra) => {
    const text = coordText(location);
    const href = mapsHref(location);
    return (
      <div className="px-3 py-2">
        <div className="flex items-center gap-2">
          <span className={`badge ${tone}`}>{label}</span>
          {extra}
        </div>
        <p className="text-sm text-gray-800 mt-1">
          {text || <span className="text-gray-400">No coordinates</span>}
        </p>
        {location?.description && (
          <p className="text-xs text-gray-500">{location.description}</p>
        )}
        {href && (
          <a href={href} target="_blank" rel="noreferrer"
             className="text-xs text-blue-600 hover:underline">Open in Google Maps</a>
        )}
      </div>
    );
  };

  return (
    <div className="pt-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500">LOCATION</p>
        <span className={`badge ${badge[0]}`}>{badge[1]}</span>
      </div>

      <div className="rounded-xl border border-gray-100 divide-y divide-gray-50">
        <div className="px-3 py-2 text-sm">
          <p className="text-gray-800">{loc.city} · {loc.cemetery}</p>
          {loc.address && <p className="text-xs text-gray-500">{loc.address}</p>}
          {order.cemeteryArea && (
            <p className="text-xs text-gray-500">Section / plot: {order.cemeteryArea}</p>
          )}
          {loc.url && (
            <a href={loc.url} target="_blank" rel="noreferrer"
               className="text-xs text-blue-600 hover:underline">Customer map link</a>
          )}
        </div>

        {block('Original', original, 'bg-gray-100 text-gray-600')}
        {block('Current', active, 'bg-blue-100 text-blue-700')}
        {proposed && block('Proposed', proposed, 'bg-amber-100 text-amber-700',
          order.locationChangeRequestedByName
            ? <span className="text-[11px] text-gray-500">
                by {order.locationChangeRequestedByName}
                {order.locationChangeRequestedAt?.toDate
                  ? ` · ${order.locationChangeRequestedAt.toDate().toLocaleString()}`
                  : ''}
              </span>
            : null)}

        {order.locationChangeNote && (
          <div className="px-3 py-2">
            <p className="text-[11px] font-semibold text-gray-400">WORKER NOTE</p>
            <p className="text-sm text-gray-700">{order.locationChangeNote}</p>
          </div>
        )}

        {order.locationChangeResolvedAt?.toDate && (
          <div className="px-3 py-2 text-xs text-gray-500">
            Customer responded {order.locationChangeResolvedAt.toDate().toLocaleString()}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-semibold text-gray-400 mb-1">AUDIT TRAIL</p>
          <ol className="rounded-xl border border-gray-100 divide-y divide-gray-50">
            {history.map((entry, i) => (
              <li key={i} className="px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-gray-700">{entry.type}</span>
                  <span className="text-gray-400">{historyTime(entry.createdAt)}</span>
                </div>
                <p className="text-gray-500">
                  {entry.changedByRole || 'unknown'}
                  {entry.changedBy ? ` · ${entry.changedBy}` : ''}
                </p>
                {entry.reason && <p className="text-gray-600 mt-0.5">{entry.reason}</p>}
                <p className="text-gray-400 mt-0.5">
                  {coordText(entry.oldLocation) || '—'} → {coordText(entry.newLocation) || '—'}
                </p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}


/**
 * Admin review of submitted work.
 *
 * Approve and reject both go through the `reviewWorkSubmission` callable:
 * `completed` is written only by the backend, which re-verifies the package's
 * proof requirements. This panel is the UI, not the access control.
 */
export function OrderReviewPanel({ order }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const status = order.status;
  const isPending = status === 'pending_admin_review';
  const wasRejected = status === 'changes_requested';
  const history = Array.isArray(order.statusHistory) ? order.statusHistory : [];

  const stamp = value => {
    if (!value) return '';
    if (typeof value === 'string') return new Date(value).toLocaleString();
    if (value.toDate) return value.toDate().toLocaleString();
    return '';
  };

  const proofOk = orderProofSatisfied(order);
  const missing = missingProofLabels(order);

  async function decide(decision) {
    setError('');
    setDone('');
    if (decision === 'reject' && reason.trim().length < 5) {
      setError('A reason of at least 5 characters is required.');
      return;
    }
    // Never fire a call that will predictably fail server-side.
    if (decision === 'approve' && !proofOk) {
      setError(`Required proof is missing. ${missing.join(', ')} required before this work can be approved.`);
      return;
    }
    setBusy(decision);
    try {
      const call = httpsCallable(functions, 'reviewWorkSubmission');
      const result = await call({ orderId: order.id, decision, reason: reason.trim() });
      setDone(result?.data?.status || 'done');
      setReason('');
    } catch (e) {
      // Surface the real Functions error code rather than a bare "internal".
      setError(reviewErrorMessage(e));
    } finally {
      setBusy('');
    }
  }

  const hasProof = Boolean(order.photos &&
    ((order.photos.beforeUrls || []).length ||
     (order.photos.afterUrls || []).length ||
     order.photos.videoUrl));
  if (!isPending && !wasRejected && history.length === 0 && !hasProof) {
    return null;
  }

  return (
    <div className="pt-4">
      <p className="text-xs font-semibold text-gray-500 mb-2">ADMIN REVIEW</p>

      {/* The approval rule is universal: 2 before + 2 after photos + a
          completion video, on every package. The verdict is stated before the
          buttons so an admin never guesses why Approve is disabled. */}
      <div className={`mb-3 rounded-xl px-3 py-2 text-sm font-semibold ${
        proofOk
          ? 'bg-green-50 text-green-700 border border-green-200'
          : 'bg-red-50 text-red-700 border border-red-200'}`}>
        {proofOk
          ? 'ADMIN PROOF COMPLETE \u2014 2 before, 2 after and a completion video'
          : `ADMIN PROOF INCOMPLETE \u2014 missing: ${missing.join(', ')}`}
      </div>

      <div className="rounded-xl border border-gray-100 divide-y divide-gray-50">
        <div className="px-3 py-2 flex items-center justify-between">
          <span className="text-sm text-gray-800">{statusLabel(status)}</span>
          {order.workerSubmittedAt && (
            <span className="text-xs text-gray-400">
              submitted {stamp(order.workerSubmittedAt)}
            </span>
          )}
        </div>

        {order.workerCompletionNote && (
          <div className="px-3 py-2">
            <p className="text-[11px] font-semibold text-gray-400">WORKER NOTE</p>
            <p className="text-sm text-gray-700">{order.workerCompletionNote}</p>
          </div>
        )}

        {order.rejectionReason && (
          <div className="px-3 py-2 bg-red-50">
            <p className="text-[11px] font-semibold text-red-500">CHANGES REQUESTED</p>
            <p className="text-sm text-red-800">{order.rejectionReason}</p>
            <p className="text-[11px] text-red-400">
              {order.rejectedByEmail || ''} {stamp(order.rejectedAt)}
            </p>
          </div>
        )}

        {order.approvedAt && (
          <div className="px-3 py-2 bg-green-50">
            <p className="text-[11px] font-semibold text-green-600">APPROVED</p>
            <p className="text-[11px] text-green-700">
              {order.approvedByEmail || ''} {stamp(order.approvedAt)}
            </p>
          </div>
        )}
      </div>

      {isPending && !proofOk && (
        <p className="text-xs text-red-600 mt-3">
          Required proof is missing. {missing.join(', ')} required before this
          work can be approved. Use Request Changes to send it back.
        </p>
      )}

      {isPending && (
        <div className="mt-3 space-y-2">
          <textarea
            className="input-field w-full"
            rows={2}
            placeholder="Reason (required to request changes) — e.g. Please upload a clearer after photo."
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              onClick={() => decide('approve')}
              disabled={Boolean(busy) || !proofOk}
              title={proofOk ? '' : 'Required proof is missing.'}
              className={`flex-1 ${proofOk ? 'btn-primary' : 'btn-secondary opacity-60 cursor-not-allowed'}`}>
              {busy === 'approve' ? '...' : 'Approve Work'}
            </button>
            <button
              onClick={() => decide('reject')}
              disabled={Boolean(busy)}
              className="btn-danger flex-1">
              {busy === 'reject' ? '...' : 'Reject / Request Changes'}
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-red-600 text-xs mt-2">{error}</p>}
      {done && <p className="text-green-600 text-xs mt-2">Order is now {statusLabel(done)}.</p>}

      {history.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-semibold text-gray-400 mb-1">REVIEW HISTORY</p>
          <ol className="rounded-xl border border-gray-100 divide-y divide-gray-50">
            {history.map((entry, i) => (
              <li key={i} className="px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-gray-700">{entry.event}</span>
                  <span className="text-gray-400">{stamp(entry.createdAt)}</span>
                </div>
                <p className="text-gray-500">
                  {entry.actorRole || 'unknown'}{entry.actorId ? ` · ${entry.actorId}` : ''}
                </p>
                {entry.reason && <p className="text-gray-600 mt-0.5">{entry.reason}</p>}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
